/**
 * Tool Session Store
 *
 * Manages collaborative tool sessions with full Supabase persistence and
 * Realtime sync.  Every participant's browser stays in sync via
 * postgres_changes subscriptions on tool_sessions and tool_session_responses.
 *
 * Public API is backward-compatible with the previous in-memory version so
 * CollaborativeSessionPanel, AggregationView, and ToolWrapper need minimal
 * changes.
 *
 * Edge case rules enforced here (Canon: 09A-session-edge-case-rules.md):
 *   C01  — participant added after 'revealed' → observer only
 *   C02  — participant added after 'finalized' → observer only
 *   C07  — all participants responded → notify facilitator
 *   C08  — zero responses when advancing to 'revealed' → warn
 *   H01  — finalize with zero responses → allowed, empty_session flagged
 *   H03  — concurrent finalize attempts → conditional write guard
 *   I01  — Realtime reconnect → full state re-fetch
 *   I02  — response DB write fail → rollback optimistic update
 */

import { dedupeById } from '../../lib/dedupeById';
import { create } from 'zustand';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { getSafeSupabaseClient, generateUUID } from '../../lib/supabaseSync';
import type {
  ToolSession,
  ToolSessionItem,
  ParticipantResponse,
  AggregateResult,
  FinalDecision,
  FacilitatorOverride,
  ToolOutputType,
  ToolResultItem,
  ToolSessionPhase,
  VoteMode,
  RemovedParticipant,
} from '../../types/prioritization/toolSession';
import {
  resolveAuthorship,
  legacyFromAuthorship,
} from '../../types/prioritization/creationProvenance';
import { getOutputTypeForSessionTool } from '../../lib/toolRegistry';
import { normalizeSessionToolId } from '../../lib/sessionToolCatalog';
import { excludeAdvisoryVoices, isCountedVoiceResponse } from '../../features/voices/advisoryFilter';
import { isParticipantResponseSubmitted } from '../../lib/liveSessionDisplay';
import { isWorkspaceUuid, resolveWorkspaceItemId } from '../../lib/resolveWorkspaceItemId';
import { useSessionStore } from './sessionStore';
import {
  buildRankingOutputFromAggregates,
  type RankingOutputFromResponses,
} from '../../lib/toolResultNormalization';

// ─── Aggregation helpers ──────────────────────────────────────────────────────

function computeAggregate(
  responses: ParticipantResponse[],
  itemId: string,
  outputType: ToolOutputType
): AggregateResult {
  const itemTitle = responses[0]?.results.find(r => r.id === itemId)?.name ?? itemId;
  const participantResults = responses
    .filter(r => r.isComplete)
    .map(r => {
      const result = r.results.find(ri => ri.id === itemId);
      return result
        ? { participantId: r.participantId, participantName: r.participantName, result }
        : null;
    })
    .filter((x): x is NonNullable<typeof x> => x !== null);

  const base: AggregateResult = { itemId, itemTitle, participantResults };
  const numeric = numericAggregate(responses, participantResults);

  if (outputType === 'classification') {
    const votes: Record<string, number> = {};
    for (const pr of participantResults) {
      const breakdown = pr.result.classificationBreakdown as {
        quadrant?: string;
        moscowCategory?: string;
      } | undefined;
      const cat =
        pr.result.category
        ?? breakdown?.quadrant
        ?? breakdown?.moscowCategory
        ?? 'unknown';
      votes[cat] = (votes[cat] ?? 0) + 1;
    }
    const majority = Object.entries(votes).sort((a, b) => b[1] - a[1])[0];
    const totalVotes = participantResults.length;
    // Grouping tools that also score (Eisenhower, impact/effort, Pareto) keep
    // the numbers too — they order items inside a group.
    return {
      ...base,
      ...(numeric ? { totalScore: numeric.totalScore, averageScore: numeric.averageScore } : {}),
      majorityCategory: majority?.[0],
      categoryVotes: votes,
      consensusStrength: majority ? majority[1] / totalVotes : 0,
    };
  }

  return numeric ? { ...base, ...numeric } : base;
}

function numericAggregate(
  responses: ParticipantResponse[],
  participantResults: AggregateResult['participantResults']
): Omit<AggregateResult, 'itemId' | 'itemTitle' | 'participantResults'> | null {
  // Prefer explicit score; fall back to Borda score derived from rank (pairwise ranking, etc.)
  const totalItems = Math.max(...participantResults.map(pr => pr.result.rank !== undefined
    ? responses.find(r => r.participantId === pr.participantId)?.results.length ?? 1
    : 1
  ));
  const scores = participantResults
    .map(pr => {
      if (pr.result.score !== undefined) return pr.result.score;
      if (pr.result.rank !== undefined) {
        // Borda score: rank 1 = highest score (totalItems), rank N = 1
        const n = responses.find(r => r.participantId === pr.participantId)?.results.length ?? totalItems;
        return n - pr.result.rank + 1;
      }
      return undefined;
    })
    .filter((s): s is number => s !== undefined);
  if (scores.length === 0) return null;
  const total = scores.reduce((a, b) => a + b, 0);
  const avg = total / scores.length;
  const variance = scores.reduce((a, s) => a + Math.pow(s - avg, 2), 0) / scores.length;
  return {
    totalScore: Math.round(total * 10) / 10,
    averageScore: Math.round(avg * 10) / 10,
    scoreSpread: Math.round(Math.sqrt(variance) * 10) / 10,
    scoreRange: { min: Math.min(...scores), max: Math.max(...scores) },
    consensusStrength: scores.length > 1 ? 1 - Math.min(Math.sqrt(variance) / (avg || 1), 1) : 1,
  };
}

// ─── Participant color palette ───────────────────────────────────────────────
const PARTICIPANT_COLORS = [
  '#6366f1', '#ec4899', '#f59e0b', '#10b981', '#3b82f6',
  '#8b5cf6', '#f97316', '#14b8a6', '#ef4444', '#84cc16',
];

function initials(name: string): string {
  return name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
}

// ─── DOM event helper ─────────────────────────────────────────────────────────
function dispatch(eventName: string, detail: Record<string, unknown>) {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(eventName, { detail }));
  }
}

// ─── DB row → domain type mappers ────────────────────────────────────────────

function rowToToolSession(row: Record<string, unknown>): ToolSession {
  return {
    id: row.id as string,
    sessionId: row.session_id as string | undefined,
    workspaceId: row.workspace_id as string | undefined,
    toolType: row.tool_type as string,
    toolName: row.tool_name as string,
    outputType: row.output_type as ToolOutputType,
    mode: 'sync',
    status: phaseToStatus(row.phase as ToolSessionPhase),
    phase: row.phase as ToolSessionPhase,
    voteMode: row.vote_mode as VoteMode,
    rationalePolicy: (row.rationale_policy as ToolSession['rationalePolicy']) ?? 'encouraged',
    participantIds: (row.participant_ids as string[]) ?? [],
    removedParticipants: (row.removed_participants as RemovedParticipant[]) ?? [],
    facilitatorId: row.facilitator_id as string,
    facilitatorName: '',
    createdAt: row.created_at as string,
    items: (row.items as ToolSessionItem[]) ?? [],
    phaseTransitionTimestamps: (row.phase_transition_timestamps as Record<string, string>) ?? {},
  };
}

function phaseToStatus(phase: ToolSessionPhase): 'collecting' | 'reviewing' | 'finalized' {
  if (phase === 'finalized') return 'finalized';
  if (phase === 'revealed') return 'reviewing';
  return 'collecting';
}

function rowToParticipantResponse(
  row: Record<string, unknown>,
  colorIndex: number
): ParticipantResponse {
  const principalType =
    (row.principal_type as 'user' | 'voice' | null) === 'voice' ? 'voice' : 'user';
  const authorship = resolveAuthorship({
    source: row.creation_source as 'human' | 'ai' | 'automated' | null | undefined,
    reviewStatus: row.creation_review_status as 'none' | 'human_reviewed' | null | undefined,
    provenance:
      (row.creation_provenance as string | null | undefined) ??
      (principalType === 'voice' ? 'ai_draft' : 'human'),
  });
  // Voice rows without explicit source still resolve as AI via legacy fallback.
  const effective =
    principalType === 'voice' && authorship.source === 'human'
      ? { source: 'ai' as const, reviewStatus: authorship.reviewStatus }
      : authorship;

  return {
    id: row.id as string,
    toolSessionId: row.tool_session_id as string,
    participantId: row.participant_id as string,
    participantName: (row.participant_name as string) || '',
    participantInitials: initials((row.participant_name as string) || 'U'),
    participantColor: PARTICIPANT_COLORS[colorIndex % PARTICIPANT_COLORS.length],
    submittedAt: (row.submitted_at as string) ?? (row.created_at as string),
    results: (row.results as ToolResultItem[]) ?? [],
    isComplete: Boolean(row.is_complete),
    rationale: (row.rationale as string | null) ?? undefined,
    principalType,
    voiceId: (row.voice_id as string | null) ?? undefined,
    sponsorUserId: (row.sponsor_user_id as string | null) ?? undefined,
    countingPolicy:
      (row.counting_policy as 'advisory' | 'counted' | null) === 'counted'
        ? 'counted'
        : 'advisory',
    creationSource: effective.source,
    creationReviewStatus: effective.reviewStatus,
    creationProvenance: legacyFromAuthorship(effective),
  };
}

/** Output type from the parent session's tool (the wizard may have stored 'ranking' generically). */
function aggregateOutputType(session: ToolSession): ToolOutputType {
  const parent = session.sessionId
    ? useSessionStore.getState().sessions.find(s => s.id === session.sessionId)
    : undefined;
  const canonical = parent ? normalizeSessionToolId(parent.selectedToolId) : undefined;
  return canonical ? getOutputTypeForSessionTool(canonical) : session.outputType;
}

// ─── Store interface ─────────────────────────────────────────────────────────

interface ToolSessionState {
  toolSessions: ToolSession[];
  participantResponses: ParticipantResponse[];
  finalDecisions: FinalDecision[];
  isLoading: boolean;
  error: string | null;

  /** Active Realtime channels keyed by tool_session_id */
  _channels: Record<string, RealtimeChannel>;

  // ── Lifecycle ──────────────────────────────────────────────────────────────

  createToolSession: (params: {
    sessionId?: string;
    trackId?: string;
    cycleId?: string;
    workspaceId: string;
    toolType: string;
    toolName: string;
    outputType: ToolOutputType;
    mode: 'sync' | 'async';
    participantIds: string[];
    participantNames: Record<string, string>;
    facilitatorId: string;
    facilitatorName: string;
    items: ToolSession['items'];
    voteMode?: VoteMode;
    rationalePolicy?: ToolSession['rationalePolicy'];
    deadline?: string;
  }) => Promise<string>;

  subscribeToToolSession: (toolSessionId: string) => Promise<void>;
  /**
   * Discover and subscribe to every tool session belonging to a parent
   * session. Needed by passive viewers (TV mode) that arrive knowing only the
   * session id — without this, their store is empty and counts stay at zero.
   */
  subscribeToSessionToolSessions: (sessionId: string) => Promise<void>;
  unsubscribeFromToolSession: (toolSessionId: string) => void;

  // ── Participant actions ────────────────────────────────────────────────────

  submitParticipantResponse: (params: {
    toolSessionId: string;
    workspaceId: string;
    participantId: string;
    participantName: string;
    results: ToolResultItem[];
    /** Optional free-text reasoning, surfaced per the session's rationale policy. */
    rationale?: string;
  }) => Promise<void>;

  /**
   * Add a participant to an active tool session.
   * Phase-gated per Canon 09A §C01/C02:
   *   collecting  → full participant, can vote
   *   revealed    → observer only, read-only
   *   finalized   → observer only, read-only
   * C09: idempotent — duplicate adds are silently ignored.
   */
  addToolSessionParticipant: (params: {
    toolSessionId: string;
    participantId: string;
    participantName: string;
  }) => Promise<{ role: 'participant' | 'observer' } | null>;

  /**
   * Remove a participant from an active tool session using the 3-tier model. Canon §C04.
   *   archived   — kept in DB, excluded from aggregate, facilitator can restore
   *   suspended  — kept in DB, excluded from aggregate, admin can restore
   *   purged     — response row deleted permanently, not tracked
   */
  removeToolSessionParticipant: (params: {
    toolSessionId: string;
    participantId: string;
    participantName: string;
    tier: 'archived' | 'suspended' | 'purged';
    removedBy: string;
  }) => Promise<void>;

  // ── Facilitator actions ────────────────────────────────────────────────────

  setPhase: (toolSessionId: string, phase: ToolSessionPhase) => Promise<void>;
  setVoteMode: (toolSessionId: string, voteMode: VoteMode) => Promise<void>;
  setRationalePolicy: (toolSessionId: string, policy: ToolSession['rationalePolicy']) => Promise<void>;

  // ── Aggregation (runs locally on synced data) ─────────────────────────────

  /**
   * Official aggregation. Humans always count; Voice responses join only
   * with a counted snapshot (PRD §4, migration 601). `excludeCountedVoices`
   * powers the results "without agent voice" comparison view — it never
   * changes what is persisted or published.
   */
  computeAggregates: (
    toolSessionId: string,
    opts?: {
      excludeCountedVoices?: boolean;
      /** Count result ids as given — criteria sittings answer about criteria, not workspace items. */
      keepResultIds?: boolean;
    },
  ) => AggregateResult[];

  finalizeDecision: (params: {
    toolSessionId: string;
    overrides: Omit<FacilitatorOverride, 'overriddenBy' | 'overriddenByName' | 'overriddenAt'>[];
    decidedBy: string;
    decidedByName: string;
    aggregates: AggregateResult[];
  }) => FinalDecision;

  // ── Selectors ─────────────────────────────────────────────────────────────

  getToolSession: (id: string) => ToolSession | undefined;
  getResponses: (toolSessionId: string) => ParticipantResponse[];
  getFinalDecision: (toolSessionId: string) => FinalDecision | undefined;

  // ── Internal mutators (used by Realtime callbacks) ─────────────────────────

  _upsertToolSession: (session: ToolSession) => void;
  _upsertParticipantResponse: (response: ParticipantResponse, colorIndex: number) => void;
}

// ─── Store implementation ────────────────────────────────────────────────────

export const useToolSessionStore = create<ToolSessionState>((set, get) => ({
  toolSessions: [],
  participantResponses: [],
  finalDecisions: [],
  isLoading: false,
  error: null,
  _channels: {},

  // ── Internal mutators ─────────────────────────────────────────────────────

  _upsertToolSession: (session) => {
    set(s => {
      const existing = s.toolSessions.findIndex(ts => ts.id === session.id);
      if (existing >= 0) {
        const updated = [...s.toolSessions];
        updated[existing] = session;
        return { toolSessions: updated };
      }
      return { toolSessions: [...s.toolSessions, session] };
    });
  },

  _upsertParticipantResponse: (response, colorIndex) => {
    set(s => {
      const existing = s.participantResponses.findIndex(
        r => r.toolSessionId === response.toolSessionId && r.participantId === response.participantId
      );
      const responses = [...s.participantResponses];
      const withColor: ParticipantResponse = {
        ...response,
        participantColor: PARTICIPANT_COLORS[colorIndex % PARTICIPANT_COLORS.length],
      };
      if (existing >= 0) {
        responses[existing] = withColor;
      } else {
        responses.push(withColor);
      }

      // Keep tool session status in sync
      const toolSessions = s.toolSessions.map(ts => {
        if (ts.id !== response.toolSessionId) return ts;
        const completedCount = responses.filter(
          r => r.toolSessionId === response.toolSessionId && r.isComplete
        ).length;
        const allIn = completedCount >= ts.participantIds.length && ts.participantIds.length > 0;
        const newStatus = allIn ? 'reviewing' as const : 'collecting' as const;

        // C07: all participants responded — notify facilitator. Canon §C07.
        if (allIn && ts.phase === 'collecting') {
          dispatch('all-participants-responded', {
            toolSessionId: ts.id,
            participantCount: ts.participantIds.length,
            message: `All ${ts.participantIds.length} participant(s) have submitted. You may now reveal results.`,
          });
        }

        return { ...ts, status: ts.phase === 'finalized' ? 'finalized' as const : newStatus };
      });

      return { participantResponses: responses, toolSessions };
    });
  },

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  createToolSession: async (params) => {
    const supabase = getSafeSupabaseClient();
    const id = generateUUID();

    // Guard against empty-string UUIDs reaching Postgres (which rejects them with
    // an opaque "invalid input syntax for type uuid" 400). Fail fast with a clear,
    // actionable message instead.
    const blank = (v: unknown) => typeof v === 'string' && v.trim() === '';
    if (blank(params.workspaceId)) {
      throw new Error('Cannot create tool session: workspace is not resolved yet. Try again in a moment.');
    }
    if (blank(params.facilitatorId)) {
      throw new Error('Cannot create tool session: facilitator (your user) is not resolved yet. Try again in a moment.');
    }
    if (blank(params.sessionId)) {
      throw new Error('Cannot create tool session: session id is missing.');
    }

    // Each id once: a tool treats items sharing an id as one (roadmap N-6).
    const items = dedupeById(params.items);

    const optimistic: ToolSession = {
      id,
      sessionId: params.sessionId,
      workspaceId: params.workspaceId,
      toolType: params.toolType,
      toolName: params.toolName,
      outputType: params.outputType,
      mode: params.mode,
      status: 'collecting',
      phase: 'collecting',
      voteMode: params.voteMode ?? 'hidden',
      rationalePolicy: params.rationalePolicy ?? 'encouraged',
      participantIds: params.participantIds,
      removedParticipants: [],
      phaseTransitionTimestamps: {},
      facilitatorId: params.facilitatorId,
      facilitatorName: params.facilitatorName,
      createdAt: new Date().toISOString(),
      items,
    };

    set(s => ({ toolSessions: [...s.toolSessions, optimistic] }));

    if (!supabase) return id;

    const { error } = await supabase.from('tool_sessions').insert({
      id,
      session_id: params.sessionId ?? null,
      workspace_id: params.workspaceId,
      tool_type: params.toolType,
      tool_name: params.toolName,
      output_type: params.outputType,
      facilitator_id: params.facilitatorId,
      mode: params.mode ?? 'sync',
      phase: 'collecting',
      vote_mode: params.voteMode ?? 'hidden',
      rationale_policy: params.rationalePolicy ?? 'encouraged',
      items,
      participant_ids: params.participantIds,
      ...(params.deadline ? { deadline: params.deadline } : {}),
    });

    if (error) {
      console.error('[ToolSessionStore] createToolSession error:', error.message);
      set(s => ({ error: error.message, toolSessions: s.toolSessions.filter(ts => ts.id !== id) }));
      throw new Error(error.message);
    }

    await get().subscribeToToolSession(id);

    if (params.sessionId && params.workspaceId) {
      void useSessionStore.getState().writeSessionEvent({
        sessionId: params.sessionId,
        workspaceId: params.workspaceId,
        actorId: params.facilitatorId,
        actorName: params.facilitatorName,
        eventType: 'session_phase_advanced',
        payload: {
          phase: 'collecting',
          tool_session_id: id,
          tool_type: params.toolType,
        },
      });
    }

    return id;
  },

  subscribeToToolSession: async (toolSessionId) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;

    // Full state fetch (also used on reconnect — I01)
    const fetchCurrentState = async () => {
      const { data: tsRow } = await supabase
        .from('tool_sessions')
        .select('*')
        .eq('id', toolSessionId)
        .single();

      if (tsRow) {
        get()._upsertToolSession(rowToToolSession(tsRow as Record<string, unknown>));
      }

      const { data: respRows } = await supabase
        .from('tool_session_responses')
        .select('*')
        .eq('tool_session_id', toolSessionId);

      const ts = get().toolSessions.find(s => s.id === toolSessionId);
      (respRows ?? []).forEach((row, idx) => {
        const colorIdx = ts
          ? ts.participantIds.indexOf(row.participant_id as string)
          : idx;
        get()._upsertParticipantResponse(
          rowToParticipantResponse(row as Record<string, unknown>, colorIdx >= 0 ? colorIdx : idx),
          colorIdx >= 0 ? colorIdx : idx
        );
      });
    };

    await fetchCurrentState();

    // Already have a channel — re-fetch was the only goal, skip re-subscribing.
    if (get()._channels[toolSessionId]) return;

    // Open Realtime channel
    const channel = supabase
      .channel(`tool-session:${toolSessionId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'tool_sessions',
          filter: `id=eq.${toolSessionId}`,
        },
        (payload) => {
          if (payload.eventType === 'UPDATE' || payload.eventType === 'INSERT') {
            get()._upsertToolSession(rowToToolSession(payload.new as Record<string, unknown>));
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'tool_session_responses',
          filter: `tool_session_id=eq.${toolSessionId}`,
        },
        (payload) => {
          if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
            const row = payload.new as Record<string, unknown>;
            const currentTs = get().toolSessions.find(s => s.id === toolSessionId);
            const colorIdx = currentTs
              ? currentTs.participantIds.indexOf(row.participant_id as string)
              : 0;
            get()._upsertParticipantResponse(
              rowToParticipantResponse(row, colorIdx >= 0 ? colorIdx : 0),
              colorIdx >= 0 ? colorIdx : 0
            );
          }
        }
      )
      // I01: Realtime reconnect — re-fetch full state to reconcile missed events.
      // Canon §I01: "reconnect triggers full re-fetch of session state."
      .on('system', {}, (payload) => {
        if (payload.status === 'SUBSCRIBED') {
          void fetchCurrentState();
        }
      })
      .subscribe();

    set(s => ({ _channels: { ...s._channels, [toolSessionId]: channel } }));
  },

  subscribeToSessionToolSessions: async (sessionId) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;
    const { data } = await supabase
      .from('tool_sessions')
      .select('id')
      .eq('session_id', sessionId);
    for (const row of data ?? []) {
      await get().subscribeToToolSession(row.id as string);
    }
  },

  unsubscribeFromToolSession: (toolSessionId) => {
    const supabase = getSafeSupabaseClient();
    const channel = get()._channels[toolSessionId];
    if (channel && supabase) {
      void supabase.removeChannel(channel);
    }
    set(s => {
      const channels = { ...s._channels };
      delete channels[toolSessionId];
      return { _channels: channels };
    });
  },

  // ── Participant actions ────────────────────────────────────────────────────

  submitParticipantResponse: async ({ toolSessionId, workspaceId, participantId, participantName, results, rationale }) => {
    const supabase = getSafeSupabaseClient();
    // ts may not be in memory yet (e.g. participant navigated directly to session).
    // Only use it for cosmetic color assignment — never gate the DB write on it.
    const ts = get().toolSessions.find(s => s.id === toolSessionId);

    const colorIdx = ts ? ts.participantIds.indexOf(participantId) : 0;

    // Optimistic update — will be rolled back on DB failure (I02)
    const optimisticResponse: ParticipantResponse = {
      id: generateUUID(),
      toolSessionId,
      participantId,
      participantName,
      participantInitials: initials(participantName),
      participantColor: PARTICIPANT_COLORS[colorIdx >= 0 ? colorIdx : 0],
      submittedAt: new Date().toISOString(),
      results,
      isComplete: true,
      rationale: rationale?.trim() ? rationale.trim() : undefined,
    };
    get()._upsertParticipantResponse(optimisticResponse, colorIdx >= 0 ? colorIdx : 0);

    if (!supabase) {
      throw new Error('Database not available');
    }

    const { error } = await supabase
      .from('tool_session_responses')
      .upsert(
        {
          tool_session_id: toolSessionId,
          workspace_id: workspaceId,
          participant_id: participantId,
          participant_name: participantName,
          results,
          is_complete: true,
          submitted_at: new Date().toISOString(),
          rationale: rationale?.trim() ? rationale.trim() : null,
        },
        { onConflict: 'tool_session_id,participant_id' }
      );

    if (error) {
      // I02: rollback optimistic update on DB failure. Canon §I02.
      console.error('[ToolSessionStore] submitParticipantResponse error:', error.message);
      set(s => ({
        participantResponses: s.participantResponses.filter(
          r => !(r.toolSessionId === toolSessionId && r.participantId === participantId && r.id === optimisticResponse.id)
        ),
        error: error.message,
      }));
      dispatch('response-save-failed', {
        toolSessionId,
        participantId,
        message: 'Your response was not saved — please try again.',
        error: error.message,
      });
      throw new Error(error.message);
    }

    let linkedSessionId = ts?.sessionId;
    let toolType = ts?.toolType ?? null;
    if (!linkedSessionId) {
      const { data: tsRow } = await supabase
        .from('tool_sessions')
        .select('session_id, tool_type')
        .eq('id', toolSessionId)
        .maybeSingle();
      linkedSessionId = (tsRow as { session_id?: string } | null)?.session_id;
      toolType = (tsRow as { tool_type?: string } | null)?.tool_type ?? toolType;
    }

    if (linkedSessionId && workspaceId) {
      void useSessionStore.getState().writeSessionEvent({
        sessionId: linkedSessionId,
        workspaceId,
        actorId: participantId,
        actorName: participantName,
        eventType: 'score_submitted',
        payload: { tool_session_id: toolSessionId, tool_type: toolType },
      });
    }
  },

  // C01/C02/C09: Phase-gated participant add. Canon §C01, §C02, §C09.
  addToolSessionParticipant: async ({ toolSessionId, participantId, participantName }) => {
    const supabase = getSafeSupabaseClient();
    const ts = get().toolSessions.find(s => s.id === toolSessionId);
    if (!ts) return null;

    // C09: idempotent — already a participant
    if (ts.participantIds.includes(participantId)) return { role: 'participant' };

    const phase = ts.phase;

    // C01: after 'revealed' → observer only
    // C02: after 'finalized' → observer only
    if (phase === 'revealed' || phase === 'finalized') {
      dispatch('participant-added-as-observer', {
        toolSessionId,
        participantId,
        participantName,
        phase,
        message: `${participantName} was added as a read-only observer. The session is in '${phase}' state; they cannot submit votes.`,
      });
      // Do not add to participant_ids — observer has no vote weight.
      // The UI reads from participantIds for vote tracking; observers are visible separately.
      return { role: 'observer' };
    }

    // Phase is 'collecting' → full participant
    const updatedIds = [...ts.participantIds, participantId];

    // Optimistic local update
    set(s => ({
      toolSessions: s.toolSessions.map(sess =>
        sess.id === toolSessionId
          ? { ...sess, participantIds: updatedIds }
          : sess
      ),
    }));

    if (supabase) {
      const { error } = await supabase
        .from('tool_sessions')
        .update({ participant_ids: updatedIds })
        .eq('id', toolSessionId);

      if (error) {
        console.error('[ToolSessionStore] addToolSessionParticipant error:', error.message);
        // Rollback
        set(s => ({
          toolSessions: s.toolSessions.map(sess =>
            sess.id === toolSessionId
              ? { ...sess, participantIds: ts.participantIds }
              : sess
          ),
        }));
        return null;
      }
    }

    return { role: 'participant' };
  },

  // C04: 3-tier participant removal. Canon §C04.
  removeToolSessionParticipant: async ({ toolSessionId, participantId, participantName, tier, removedBy }) => {
    const supabase = getSafeSupabaseClient();
    const ts = get().toolSessions.find(s => s.id === toolSessionId);
    if (!ts) return;

    if (tier === 'purged') {
      // Tier 3: delete the response row permanently.
      if (supabase) {
        const { error } = await supabase
          .from('tool_session_responses')
          .delete()
          .eq('tool_session_id', toolSessionId)
          .eq('participant_id', participantId);
        if (error) {
          console.error('[ToolSessionStore] C04 purge error:', error.message);
          return;
        }
      }
      // Remove from in-memory responses
      set(s => ({
        participantResponses: s.participantResponses.filter(
          r => !(r.toolSessionId === toolSessionId && r.participantId === participantId)
        ),
      }));
      dispatch('participant-purged', { toolSessionId, participantId, participantName });
    } else {
      // Tier 1 (archived) or Tier 2 (suspended): keep responses in DB, track exclusion.
      const removal: RemovedParticipant = {
        participantId,
        participantName,
        tier,
        removedAt: new Date().toISOString(),
        removedBy,
      };
      const updatedRemoved = [...(ts.removedParticipants ?? []), removal];

      // Persist to DB
      if (supabase) {
        const { error } = await supabase
          .from('tool_sessions')
          .update({ removed_participants: updatedRemoved })
          .eq('id', toolSessionId);
        if (error) {
          console.error('[ToolSessionStore] C04 archive/suspend error:', error.message);
          return;
        }
      }

      // Update in-memory session
      set(s => ({
        toolSessions: s.toolSessions.map(sess =>
          sess.id === toolSessionId
            ? { ...sess, removedParticipants: updatedRemoved }
            : sess
        ),
      }));

      dispatch(tier === 'archived' ? 'participant-archived' : 'participant-suspended', {
        toolSessionId,
        participantId,
        participantName,
        tier,
        message: `${participantName} was ${tier}. Their responses are retained but excluded from the aggregate. A workspace admin can restore them.`,
      });
    }

    // Remove from active participant_ids in both cases
    const updatedIds = ts.participantIds.filter(id => id !== participantId);
    if (supabase) {
      await supabase
        .from('tool_sessions')
        .update({ participant_ids: updatedIds })
        .eq('id', toolSessionId);
    }
    set(s => ({
      toolSessions: s.toolSessions.map(sess =>
        sess.id === toolSessionId
          ? { ...sess, participantIds: updatedIds }
          : sess
      ),
    }));
  },

  // ── Facilitator actions ────────────────────────────────────────────────────

  setPhase: async (toolSessionId, phase) => {
    const supabase = getSafeSupabaseClient();
    const ts = get().toolSessions.find(s => s.id === toolSessionId);
    if (!ts) return;

    // C05/C06: verify facilitator is still an active workspace member. Canon §C05/C06.
    if (supabase && ts.workspaceId) {
      const { data: membership, error: memberErr } = await supabase
        .from('workspace_memberships')
        .select('user_id')
        .eq('workspace_id', ts.workspaceId)
        .eq('user_id', ts.facilitatorId)
        .maybeSingle();

      if (memberErr) {
        console.warn('[ToolSessionStore] C05/C06: could not verify facilitator membership:', memberErr.message);
        // Non-blocking — proceed; RLS will enforce at DB write
      } else if (!membership) {
        dispatch('facilitator-access-lost', {
          toolSessionId,
          facilitatorId: ts.facilitatorId,
          message: 'The facilitator no longer has workspace access. Phase transitions are blocked. A workspace admin must reassign the facilitator.',
        });
        return;
      }
    }

    // C08: warn if advancing to 'revealed' with zero responses. Canon §C08.
    if (phase === 'revealed') {
      const responses = get().participantResponses.filter(
        r => r.toolSessionId === toolSessionId && r.isComplete
      );
      if (responses.length === 0) {
        dispatch('zero-responses-phase-advance', {
          toolSessionId,
          phase,
          message: 'No responses were recorded for this session. Advancing to revealed anyway.',
        });
        // Allow — C08 says the facilitator can proceed; warning is advisory.
      }
    }

    // I03: record the timestamp when each phase ends, so aggregate can filter late responses.
    // collecting_ended_at is set when we leave 'collecting'. Canon §I03 / §X04.
    const now = new Date().toISOString();
    const existingTimestamps = ts.phaseTransitionTimestamps ?? {};
    const updatedTimestamps: Record<string, string> = { ...existingTimestamps };
    if (ts.phase === 'collecting' && phase !== 'collecting') {
      updatedTimestamps.collecting_ended_at = now;
    }
    if (ts.phase === 'revealed' && phase !== 'revealed') {
      updatedTimestamps.revealed_ended_at = now;
    }
    const timestampsChanged = Object.keys(updatedTimestamps).length !== Object.keys(existingTimestamps).length;

    // H03: concurrent finalize guard — use conditional write. Canon §H03.
    // If another actor already finalized, our update must not overwrite their finalized state.
    if (supabase && phase !== 'finalized') {
      // For non-finalize transitions: standard update (include timestamp if changed)
      const { error } = await supabase
        .from('tool_sessions')
        .update({
          phase,
          ...(timestampsChanged && { phase_transition_timestamps: updatedTimestamps }),
        })
        .eq('id', toolSessionId);

      if (error) {
        console.error('[ToolSessionStore] setPhase error:', error.message);
        return;
      }
    } else if (supabase && phase === 'finalized') {
      // H03: only update if not already finalized (conditional write).
      const { data, error } = await supabase
        .from('tool_sessions')
        .update({
          phase: 'finalized',
          ...(timestampsChanged && { phase_transition_timestamps: updatedTimestamps }),
        })
        .eq('id', toolSessionId)
        .neq('phase', 'finalized')
        .select('id');

      if (error) {
        console.error('[ToolSessionStore] setPhase (finalize) error:', error.message);
        return;
      }
      if (!data || data.length === 0) {
        // Concurrent finalize — another actor already finalized
        dispatch('concurrent-finalize-conflict', {
          toolSessionId,
          message: 'This session was already finalized by another actor. Refreshing state.',
        });
        // Re-fetch to reconcile
        void get().subscribeToToolSession(toolSessionId);
        return;
      }
    }

    // Optimistic local update (after DB succeeds)
    set(s => ({
      toolSessions: s.toolSessions.map(ts2 =>
        ts2.id === toolSessionId
          ? {
              ...ts2,
              phase,
              status: phaseToStatus(phase),
              ...(timestampsChanged && { phaseTransitionTimestamps: updatedTimestamps }),
            }
          : ts2
      ),
    }));
  },

  setVoteMode: async (toolSessionId, voteMode) => {
    const supabase = getSafeSupabaseClient();

    set(s => ({
      toolSessions: s.toolSessions.map(ts =>
        ts.id === toolSessionId ? { ...ts, voteMode } : ts
      ),
    }));

    if (!supabase) return;
    const { error } = await supabase
      .from('tool_sessions')
      .update({ vote_mode: voteMode })
      .eq('id', toolSessionId);

    if (error) {
      console.error('[ToolSessionStore] setVoteMode error:', error.message);
    }
  },

  setRationalePolicy: async (toolSessionId, policy) => {
    const supabase = getSafeSupabaseClient();

    set(s => ({
      toolSessions: s.toolSessions.map(ts =>
        ts.id === toolSessionId ? { ...ts, rationalePolicy: policy } : ts
      ),
    }));

    if (!supabase) return;
    const { error } = await supabase
      .from('tool_sessions')
      .update({ rationale_policy: policy })
      .eq('id', toolSessionId);

    if (error) {
      console.error('[ToolSessionStore] setRationalePolicy error:', error.message);
    }
  },

  // ── Aggregation ───────────────────────────────────────────────────────────

  computeAggregates: (toolSessionId, opts) => {
    const session = get().toolSessions.find(s => s.id === toolSessionId);
    if (!session) return [];

    const outputType = aggregateOutputType(session);
    const parent = session.sessionId
      ? useSessionStore.getState().sessions.find(s => s.id === session.sessionId)
      : undefined;

    // C04: exclude responses from archived/suspended removed participants. Canon §C04.
    // Purged participants already have their responses deleted from DB/memory.
    const removedIds = new Set(
      (session.removedParticipants ?? []).map(rp => rp.participantId)
    );

    // I03: phase-gated aggregate — exclude responses committed after collecting ended. Canon §I03.
    // Responses that raced past the collecting→revealed transition are excluded from the aggregate;
    // they are committed to DB as historical facts but do not affect results.
    const collectingEndedAt = session.phaseTransitionTimestamps?.collecting_ended_at;
    const collectingDeadline = collectingEndedAt ? new Date(collectingEndedAt).getTime() : null;

    // Voices Phase 2 (PRD §4): counted math = humans + counted-snapshot Voice
    // rows. Advisory Voice rows never enter; the opt-out flag supports the
    // results "without agent voice" comparison view.
    const officialResponses = excludeAdvisoryVoices(
      get().participantResponses.filter(r => {
        if (r.toolSessionId !== toolSessionId) return false;
        if (!isParticipantResponseSubmitted(r, outputType)) return false;
        if (removedIds.has(r.participantId)) return false;
        // I03: if collection window has closed, exclude responses submitted after it.
        if (collectingDeadline !== null) {
          const submittedAt = r.submittedAt ? new Date(r.submittedAt).getTime() : null;
          if (submittedAt !== null && submittedAt > collectingDeadline) return false;
        }
        return true;
      }),
    );
    const responses = opts?.excludeCountedVoices
      ? officialResponses.filter((r) => !isCountedVoiceResponse(r))
      : officialResponses;
    if (responses.length === 0) return [];

    // Same ID remapping as Close & save — tool-local ids (item-0) and name
    // matches must land on one workspace item so interim totals match final.
    const idResolverCtx = {
      sessionItemIds: parent?.itemIds ?? [],
      toolSessionItems: session.items.map(i => {
        const workspaceId =
          (isWorkspaceUuid(i.id) && i.id) ||
          (i.sourceId && isWorkspaceUuid(i.sourceId) ? i.sourceId : null) ||
          i.id;
        return { id: workspaceId, name: i.title };
      }),
    };
    const normalizedResponses = opts?.keepResultIds ? responses : responses.map(r => ({
      ...r,
      results: r.results.map(ri => {
        const resolvedId = resolveWorkspaceItemId(ri.id, ri.name, idResolverCtx) ?? ri.id;
        return resolvedId === ri.id ? ri : { ...ri, id: resolvedId };
      }),
    }));

    const allItemIds = new Set<string>();
    normalizedResponses.forEach(r => r.results.forEach(ri => allItemIds.add(ri.id)));
    return Array.from(allItemIds).map(itemId =>
      computeAggregate(normalizedResponses, itemId, outputType)
    );
  },

  finalizeDecision: ({ toolSessionId, overrides, decidedBy, decidedByName, aggregates }) => {
    const session = get().toolSessions.find(s => s.id === toolSessionId);
    if (!session) throw new Error('Tool session not found');
    const responses = get().participantResponses.filter(r => r.toolSessionId === toolSessionId);

    // H01: detect empty session (zero responses). Canon §H01.
    const isEmpty = responses.length === 0;
    if (isEmpty) {
      dispatch('empty-session-finalized', {
        toolSessionId,
        message: 'This session was finalized with no participant responses.',
      });
    }

    const overrideMap = new Map(overrides.map(o => [o.itemId, o]));
    const now = new Date().toISOString();

    const finalResults: ToolResultItem[] = aggregates.map(agg => {
      const override = overrideMap.get(agg.itemId);
      if (override) return { ...override.overrideResult, id: agg.itemId, name: agg.itemTitle };
      if (session.outputType === 'classification') {
        return { id: agg.itemId, name: agg.itemTitle, category: agg.majorityCategory };
      }
      return { id: agg.itemId, name: agg.itemTitle, score: agg.averageScore };
    });

    const unanimousItems = aggregates.filter(a =>
      a.consensusStrength !== undefined && a.consensusStrength >= 0.99
    ).length;
    const contestedItems = aggregates.filter(a =>
      a.consensusStrength !== undefined && a.consensusStrength < 0.5
    ).length;

    const stampedOverrides: FacilitatorOverride[] = overrides.map(o => ({
      ...o,
      overriddenBy: decidedBy,
      overriddenByName: decidedByName,
      overriddenAt: now,
    }));

    const decision: FinalDecision = {
      toolSessionId,
      decisionType: overrides.length > 0 ? 'facilitator-override' : 'accepted-aggregate',
      finalResults,
      overrides: stampedOverrides,
      decidedBy,
      decidedByName,
      decidedAt: now,
      provenanceSummary: {
        participantCount: responses.length,
        unanimousItems,
        overriddenItems: overrides.length,
        contestedItems,
        // H01: empty_session flag so downstream consumers can distinguish "no data" decisions
        ...(isEmpty ? { emptySession: true } : {}),
      },
    };

    set(s => ({
      finalDecisions: [...s.finalDecisions.filter(d => d.toolSessionId !== toolSessionId), decision],
      toolSessions: s.toolSessions.map(sess =>
        sess.id === toolSessionId
          ? { ...sess, status: 'finalized' as const, phase: 'finalized' as const }
          : sess
      ),
    }));

    // §2.1 — Persist aggregate_result and facilitator_overrides to DB before phase transition.
    // These are durable governance artifacts; Zustand-only storage violates the build contract.
    const supabase = getSafeSupabaseClient();
    if (supabase) {
      const aggregateResult = {
        tool_type: session.toolType,
        participant_count: responses.length,
        items: finalResults.map((item, idx) => ({
          item_id: item.id,
          name: item.name,
          rank: item.rank ?? idx + 1,
          ...(item.score !== undefined && { score: item.score }),
          ...(item.category !== undefined && { category: item.category }),
          ...(item.scoreBreakdown !== undefined && { score_breakdown: item.scoreBreakdown }),
          ...(item.metadata !== undefined && { metadata: item.metadata }),
        })),
        provenance: decision.provenanceSummary,
        decided_at: decision.decidedAt,
      };

      const facilitatorOverrides = stampedOverrides.length > 0 ? {
        overrides: stampedOverrides.map(o => ({
          item_id: o.itemId,
          override_result: o.overrideResult,
          overridden_by: o.overriddenBy,
          overridden_by_name: o.overriddenByName,
          overridden_at: o.overriddenAt,
        })),
      } : null;

      void supabase
        .from('tool_sessions')
        .update({
          aggregate_result: aggregateResult,
          ...(facilitatorOverrides !== null && { facilitator_overrides: facilitatorOverrides }),
        })
        .eq('id', toolSessionId)
        .neq('phase', 'finalized'); // H03: don't overwrite a concurrent finalize
    }

    // Persist phase=finalized (H03 guard is inside setPhase)
    void get().setPhase(toolSessionId, 'finalized');

    return decision;
  },

  // ── Selectors ─────────────────────────────────────────────────────────────

  getToolSession: (id) => get().toolSessions.find(s => s.id === id),
  getResponses: (toolSessionId) =>
    get().participantResponses.filter(r => r.toolSessionId === toolSessionId),
  getFinalDecision: (toolSessionId) =>
    get().finalDecisions.find(d => d.toolSessionId === toolSessionId),
}));

/**
 * Close & save for a sitting with many people's answers: re-read the answers
 * from the database (never trust what this tab happened to load, §2.1), count
 * them with the counting rule (`computeAggregates` — removed people, late
 * answers and advisory agent voices left out), and build what `run_session`
 * saves. Null when nobody has answered yet.
 */
export async function rankingOutputForToolSession(
  toolSessionId: string,
  meta: { toolId: string; toolName: string; keepResultIds?: boolean },
): Promise<RankingOutputFromResponses | null> {
  const store = useToolSessionStore.getState();
  await store.subscribeToToolSession(toolSessionId);
  const session = store.getToolSession(toolSessionId);
  if (!session) return null;
  const aggregates = store.computeAggregates(toolSessionId, { keepResultIds: meta.keepResultIds });
  if (aggregates.length === 0) return null;
  const responseCount = new Set(
    aggregates.flatMap(a => a.participantResults.map(pr => pr.participantId)),
  ).size;
  return buildRankingOutputFromAggregates(aggregates, aggregateOutputType(session), {
    toolId: meta.toolId,
    toolName: meta.toolName,
    responseCount,
  });
}
